import {
    User,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    sendPasswordResetEmail,
    updateEmail,
    updatePassword,
    updateProfile,
    UserCredential,
    sendEmailVerification,
    GoogleAuthProvider,
    signInWithPopup
} from 'firebase/auth';
import { useLocalStorage } from 'hooks';
import React, { useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { auth } from '../firebase';
import { useMutation, useLazyQuery } from '@apollo/client';
import { checkUserExistanceQuery } from 'graphql/queries';
import { insertUser as insertUserMutation } from 'graphql/mutations';
import { InsertUser, InsertUserVariables } from 'graphql/mutations/types/InsertUser';
import { UserExistanceCheck, UserExistanceCheckVariables } from 'graphql/queries/types/UserExistanceCheck';
import { FirebaseError } from 'firebase/app';
import { SquareLoader } from 'components';

interface AuthContextType {
    currentUser: User | null;
    lastEmailVerificationSentAt: Date | null;
    login: (email: string, password: string) => Promise<UserCredential>;
    signup: (email: string, password: string, displayName: string) => Promise<UserCredential>;
    logout: () => void;
    resetPassword: (email: string) => Promise<void>;
    updateUserEmail: (email: string) => Promise<void>;
    updateUserDisplayName: (displayName: string) => Promise<string>;
    sendVerifyEmail: () => Promise<void>;
    updateUserPassword: (password: string) => Promise<void>;
    signInWithGoogle: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextType | null>(null);

export function useAuth() {
    return useContext(AuthContext)!;
}

function getResolvedDisplayName(user: User, preferredDisplayName?: string) {
    const normalizedPreferredDisplayName = preferredDisplayName?.trim();
    const normalizedUserDisplayName = user.displayName?.trim();
    const normalizedProviderDisplayName = user.providerData.find((provider) => provider.displayName?.trim())?.displayName?.trim();
    const normalizedEmail = user.email?.trim();
    const normalizedProviderEmail = user.providerData.find((provider) => provider.email?.trim())?.email?.trim();

    return normalizedPreferredDisplayName || normalizedUserDisplayName || normalizedProviderDisplayName || normalizedEmail || normalizedProviderEmail || user.uid;
}

export function AuthProvider({ children }: { children?: ReactNode | undefined }) {
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [newUserSignedUpWithEmail, setNewUserSignedUpWithEmail] = useState(false);
    const [newUserDisplayName, setNewUserDisplayName] = useState('');
    const [lastEmailVerificationSentAt, setLastEmailVerificationSentAt] = useLocalStorage<Date | null>('verification-email-sent-at', null);

    const [insertUser] = useMutation<InsertUser, InsertUserVariables>(insertUserMutation);
    const [checkUserExistance] = useLazyQuery<UserExistanceCheck, UserExistanceCheckVariables>(checkUserExistanceQuery);

    const ensureDbUser = useCallback(async (user: User, preferredDisplayName?: string) => {
        const resolvedDisplayName = getResolvedDisplayName(user, preferredDisplayName);

        try {
            const { data: dbUser, error: queryError } = await checkUserExistance({
                variables: { authId: user.uid }
            });

            if (queryError) {
                console.error('Error checking user existance', queryError);
                return;
            }

            if (!dbUser || dbUser.quiz_master_users.length === 0) {
                await insertUser({
                    variables: {
                        authId: user.uid,
                        displayName: resolvedDisplayName
                    }
                });
            }
        } catch (error) {
            console.error('Error ensuring user in database', error);
        }
    }, [checkUserExistance, insertUser]);

    async function signup(email: string, password: string, displayName: string) {
        setNewUserSignedUpWithEmail(true);
        const normalizedDisplayName = displayName.trim();
        setNewUserDisplayName(normalizedDisplayName);

        const userCredential = await createUserWithEmailAndPassword(auth, email, password);

        if (normalizedDisplayName && userCredential.user.displayName !== normalizedDisplayName) {
            await updateProfile(userCredential.user, { displayName: normalizedDisplayName });
        }

        await ensureDbUser(userCredential.user, normalizedDisplayName);

        return userCredential;
    }

    function login(email: string, password: string) {
        return signInWithEmailAndPassword(auth, email, password);
    }

    const signInWithGoogle = useCallback(async () => {
        try {
            const provider = new GoogleAuthProvider();
            // Using a popup (rather than signInWithRedirect) keeps the user on the current
            // subdomain (e.g. app.avronquizzes.me) for the whole flow, avoiding full-page
            // navigations through the Firebase authDomain that can otherwise land the user
            // back on the wrong domain. It also avoids relying on cross-origin storage
            // access, which many browsers now block by default.
            const result = await signInWithPopup(auth, provider);
            if (result && !result.user.isAnonymous) {
                const user = result.user;

                await ensureDbUser(user);
                setCurrentUser(user);
            }
        } catch (error) {
            const typedError: FirebaseError = error as FirebaseError;
            // Handle Errors here.
            const errorCode = typedError.code;
            const errorMessage = typedError.message;
            // The email of the user's account used.
            const email = typedError.customData?.email;
            // The AuthCredential type that was used.
            const credential = GoogleAuthProvider.credentialFromError(typedError);
            console.error('Google Sign In Error', errorCode, errorMessage, email, credential);
            throw typedError;
        }
    }, [ensureDbUser]);

    const sendVerifyEmail = useCallback(async () => {
        if (currentUser) {
            setLastEmailVerificationSentAt(new Date());
            await sendEmailVerification(currentUser!);
        }
        return Promise.reject();
    }, [currentUser, setLastEmailVerificationSentAt]);

    function logout() {
        auth.signOut();
        setCurrentUser(null);
    }

    function resetPassword(email: string) {
        return sendPasswordResetEmail(auth, email);
    }

    const updateUserEmail = useCallback(
        async (email: string) => {
            if (currentUser) {
                return updateEmail(currentUser, email);
            }
            return Promise.reject();
        },
        [currentUser]
    );

    const updateUserPassword = useCallback(
        async (password: string) => {
            if (currentUser) {
                return updatePassword(currentUser, password);
            }
            return Promise.reject();
        },
        [currentUser]
    );

    const updateUserDisplayName = useCallback(
        async (displayName: string): Promise<string> => {
            if (currentUser) {
                try {
                    await updateProfile(currentUser, { displayName });
                } catch (error) {
                    return "Update failed";
                }
                setCurrentUser({
                    ...currentUser,
                    displayName
                });
            }
            return "Update successful";
        },
        [currentUser]
    );

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            setCurrentUser(user);
            setLoading(false);

            void (async () => {
                if (newUserSignedUpWithEmail && user) {
                    setLastEmailVerificationSentAt(new Date());
                    await sendEmailVerification(user);
                    setNewUserSignedUpWithEmail(false);
                }

                if (newUserDisplayName && user && user.displayName !== newUserDisplayName) {
                    await updateProfile(user, { displayName: newUserDisplayName });
                }

                if (user) {
                    await ensureDbUser(user, newUserDisplayName || undefined);
                }

                if (!user || newUserDisplayName) {
                    setNewUserDisplayName('');
                }
            })().catch((error) => {
                console.error('Error handling auth state bootstrap', {
                    userId: user?.uid,
                    hasNewUserDisplayName: !!newUserDisplayName,
                    newUserSignedUpWithEmail,
                    error
                });
            });
        });

        return unsubscribe;
    }, [newUserDisplayName, newUserSignedUpWithEmail, ensureDbUser, setLastEmailVerificationSentAt]);

    const contextValue = {
        currentUser,
        lastEmailVerificationSentAt,
        login,
        signup,
        logout,
        resetPassword,
        updateUserEmail,
        updateUserDisplayName,
        updateUserPassword,
        sendVerifyEmail,
        signInWithGoogle
    };

    return (
        <AuthContext.Provider value={contextValue}>
            {loading && <SquareLoader color='black' />}
            {!loading && children}
        </AuthContext.Provider>
    );
}
